// src/apiService.ts

// Import types from the new central types file
import {
  RaceResult, // Re-added as it's now used in the mapping
  ApiResponse,
  // CurrentInstanceData, // Removed as it's not directly used here
  JockeyRacesRequestPayload,
} from './types';

const API_ENDPOINT = '/api/v1/races/jockey-search';

/**
 * Fetches jockey race data from the API.
 * @param payload - The data to send for the race search/refresh.
 * @returns A Promise that resolves to the API response.
 */
export const fetchJockeyRaces = async (
  payload: JockeyRacesRequestPayload
): Promise<ApiResponse> => {
  try {
    // Construct the body payload to primarily contain jockeyNames
    const bodyPayload = { jockeyNames: payload.jockeyNames };

    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json', // Good practice to include Accept header
      },
      body: JSON.stringify(bodyPayload),
    });

    if (!response.ok) {
      // Attempt to get more specific error message from API if possible
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        // Ignore if response is not JSON
      }
      const errorMessage =
        errorData?.error ||
        response.statusText ||
        `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    // Define the backend result type for clarity
    interface BackendJockeyRaceResult {
      searchJockey: string;
      jockeyNameFromMeta: string;
      eventName: string;
      marketName: string;
      runnerName: string;
      backOdds: number | null;
      layOdds: number | null;
      raceNumber: string | null;
      marketStartTime: string;
      liquidity: number | null;
      selectionId: number;
    }

    const dataFromBackend = await response.json();

    // Check for backend success status
    if (!dataFromBackend.success) {
      throw new Error(
        dataFromBackend.error || 'API returned an unsuccessful status.'
      );
    }

    // Map the backend results to frontend RaceResult objects
    const mappedResults: RaceResult[] = (dataFromBackend.results || []).map(
      (backendResult: BackendJockeyRaceResult) => ({
        searchJockey: backendResult.searchJockey,
        jockey: backendResult.jockeyNameFromMeta, // Map jockeyNameFromMeta to jockey
        event: backendResult.eventName, // Map eventName to event
        market: backendResult.marketName, // Map marketName to market
        runner: backendResult.runnerName, // Map runnerName to runner
        backOdds:
          backendResult.backOdds !== null ? String(backendResult.backOdds) : '', // Convert number to string, handle null
        layOdds:
          backendResult.layOdds !== null ? String(backendResult.layOdds) : '', // Convert number to string, handle null
        raceNumber: backendResult.raceNumber || '', // Handle null for raceNumber
        marketStartTime: backendResult.marketStartTime,
        liquidity:
          backendResult.liquidity !== null
            ? backendResult.liquidity
            : undefined, // Handle null for liquidity
        selectionId: backendResult.selectionId,
      })
    );

    const finalApiResponse: ApiResponse = { results: mappedResults };

    // Basic validation of the expected structure
    if (!finalApiResponse || !Array.isArray(finalApiResponse.results)) {
      throw new Error('Invalid data structure received from server.');
    }

    // Further validation for each RaceResult can be added here if necessary
    // For example, checking for essential properties on each result item.

    return finalApiResponse;
  } catch (error: any) {
    console.error('API Service Error:', error);
    // Re-throw the error so the calling component can handle it
    // Or, you could return a structured error response:
    // return { results: [], error: error.message || 'Unknown API error' };
    throw error;
  }
};
